/**
 * ドメイン管理事業者ごとの案内文（05-ui.md §8.2「あなたがやること」ボックス）。
 *
 * **設定する値そのものは事業者に依らず同一**。ここで出し分けるのは
 * 「その事業者の管理画面のどこを開くか」という導線だけ。
 * 値を事業者ごとに変えてはいけない（検証ジョブが照合する値と食い違う）。
 */

export interface DnsProvider {
  id: string;
  name: string;
  /** 管理画面の入口。null なら「ご契約の管理画面」とだけ案内する。 */
  consoleUrl: string | null;
  /** その事業者でのメニュー名。契約者が画面上で探す文言に合わせる。 */
  steps: string[];
  /** その事業者固有の注意点。無ければ null。 */
  caution: string | null;
}

export const DNS_PROVIDERS: readonly DnsProvider[] = [
  {
    id: 'onamae',
    name: 'お名前.com',
    consoleUrl: 'https://navi.onamae.com/domain/setting/dns',
    steps: [
      'お名前.com Navi にログインします。',
      '「ドメイン」→「DNS」→「DNSレコード設定を利用する」を選びます。',
      '対象のドメインを選んで「次へ」を押します。',
      '下の表の 1 行ずつを「ホスト名 / TYPE / VALUE / TTL」に入力し、「追加」を押します。',
      'すべて追加したら、ページ下部の「確認画面へ進む」→「設定する」を押します。',
    ],
    caution:
      'ホスト名の欄には、ドメイン名を除いた部分だけを入れます（例: `_dmarc.example.jp` なら `_dmarc`）。',
  },
  {
    id: 'muumuu',
    name: 'ムームードメイン',
    consoleUrl: 'https://muumuu-domain.com/checkout/domain/list',
    steps: [
      'コントロールパネルにログインします。',
      '「ドメイン操作」→「ムームーDNS」を開きます。',
      '対象ドメインの「変更」を押します。',
      '「カスタム設定」のセットアップ情報を変更で、下の表のとおりに追加します。',
      '「セットアップ情報変更」を押して保存します。',
    ],
    caution: 'ムームーDNS の「サブドメイン」欄が、下の表の「ホスト名」に当たります。',
  },
  {
    id: 'xserver',
    name: 'エックスサーバー / Xserver ドメイン',
    consoleUrl: 'https://secure.xserver.ne.jp/xapanel/login/xdomain/',
    steps: [
      'Xserver アカウントにログインします。',
      '「ドメイン」→「DNSレコード設定」を開きます。',
      '対象ドメインを選び、「DNSレコード追加」タブを開きます。',
      '下の表のとおりに 1 件ずつ追加します。',
    ],
    caution:
      '同じサーバーでウェブサイトも運用している場合、既存の MX レコードを必ず削除してください（併存すると配送先が分かれます）。',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    consoleUrl: 'https://dash.cloudflare.com/',
    steps: [
      'Cloudflare のダッシュボードにログインします。',
      '対象のサイトを選び、「DNS」→「Records」を開きます。',
      '「Add record」から下の表のとおりに追加します。',
      'MX と TXT では Proxy 設定は使いません（オレンジの雲は付けません）。',
    ],
    caution:
      'CNAME を追加するときは Proxy status を必ず「DNS only」（灰色の雲）にしてください。オレンジのままだとメールソフトの自動設定が失敗します。',
  },
  {
    id: 'route53',
    name: 'Amazon Route 53',
    consoleUrl: 'https://console.aws.amazon.com/route53/v2/hostedzones',
    steps: [
      'Route 53 のコンソールで対象の Hosted zone を開きます。',
      '「Create record」を押します。',
      'Record name / Record type / Value を下の表のとおりに入力します。',
      'TXT の値は前後をダブルクォートで囲みます。',
    ],
    caution:
      'MX レコードの値は「優先度 スペース ホスト名」の形式（例: `10 mx1.example.net`）で入力します。',
  },
  {
    id: 'sakura',
    name: 'さくらのドメイン / さくらのクラウド',
    consoleUrl: 'https://secure.sakura.ad.jp/domain/',
    steps: [
      '会員メニューから「ドメイン/SSL」を開きます。',
      '対象ドメインの「ゾーン編集」を開きます。',
      '「エントリの追加」で下の表のとおりに追加します。',
      '最後に「データ送信」を押して反映します。',
    ],
    caution: null,
  },
  {
    id: 'other',
    name: 'その他 / 分からない',
    consoleUrl: null,
    steps: [
      'ドメインを購入した会社の管理画面にログインします。',
      '「DNS設定」「ゾーン編集」「ネームサーバー設定」などの名前のメニューを開きます。',
      '下の表のとおりにレコードを追加します。',
    ],
    caution:
      'どこで取得したか分からない場合は、ドメイン購入時の控えメールを探すか、サポートまでお問い合わせください。',
  },
];

export function findProvider(id: string): DnsProvider {
  // 未知の id は「その他」に寄せる。ここで undefined を返すと画面が空になる。
  return DNS_PROVIDERS.find((p) => p.id === id) ?? DNS_PROVIDERS[DNS_PROVIDERS.length - 1]!;
}

const STORAGE_KEY = 'mail-sv.portal.dns-provider';

export function loadPreferredProvider(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? 'other';
  } catch {
    return 'other';
  }
}

export function savePreferredProvider(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* プライベートモードでも動作は続ける */
  }
}
